import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Launch as LaunchIcon } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';

import MessagesTable from '../../components/MessagesTable/MessagesTable';

import { compareDates, formatUnixDate } from '../../../util/HelperFunctions';
import { IMPORTANCE_TYPES, USER_ROLES } from '../../../util/constants/AppConstants';

import './Dashboard.scss';

const Dashboard = () => {
  const feedbackState = useSelector((state) => state.data.feedback);
  const loading = useSelector((state) => state.data.feedbackLoading);

  const { userInfo } = useOutletContext();

  const stafferRoleValue = USER_ROLES.find((role) => role.short === 'Constituent Staff').value;
  const managerRoleValue = USER_ROLES.find((role) => role.short === 'Manager').value;
  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councilorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councilorRoleValue;

  const sortedFeedback = useMemo(() => {
    return [...feedbackState].sort((a, b) => b.importance - a.importance);
  }, [feedbackState]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'case_id',
        header: 'Case',
        minSize: 50,
        muiTableBodyCellProps: {
          sx: {
            padding: '0.5rem 0.75rem',
            border: 0,
          },
        },
        filterVariant: 'select',
        filterSelectOptions: [
          { label: 'All', value: 'all' },
          { label: 'With Case', value: 'with' },
          { label: 'Without Case', value: 'without' },
        ],
        filterFn: (row, columnId, filterValue) => {
          const value = row.getValue(columnId);

          if (filterValue === 'with') return value !== null;
          if (filterValue === 'without') return value === null;

          return true;
        },
        Cell: ({ row }) => {
          const caseId = row.original.case_id;

          if (!caseId) return null;

          return (
            <Tooltip title="Open Case">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/case/${row.original.id.split('-')[1]}`, '_blank');
                }}
              >
                <LaunchIcon />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        accessorFn: (row) => {
          const { importance } = row;
          const option = IMPORTANCE_TYPES.find((option) => option.value === importance);
          return option ? option.label : '';
        },
        id: 'importance',
        header: 'Importance',
        minSize: 70,
        grow: 1,
        filterVariant: 'select',
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.importance;
          const b = rowB.original.importance;

          if (a === b) return 0;
          if (a === null || a === undefined) return 1;
          if (b === null || b === undefined) return -1;
          return a - b;
        },
      },
      {
        accessorFn: (row) => {
          const areaNumber = row[isCouncillor ? 'ward_nr' : 'poll_label'];
          return areaNumber ? `${isCouncillor ? 'Ward' : 'Poll'} - ${areaNumber}` : null;
        },
        id: `${isCouncillor ? 'ward' : 'poll'}_label`,
        header: isCouncillor ? 'Ward' : 'Poll',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
      },
      {
        accessorKey: 'type',
        header: 'Category',
        minSize: 150,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        minSize: 150,
        grow: 5,
        enableSorting: false,
      },
      {
        accessorKey: 'source',
        header: 'Source',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'staffer_fullname',
        header: 'Added by',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'date_utc',
        header: 'Date',
        filterVariant: 'date',
        Cell: ({ cell }) => formatUnixDate(cell.getValue(), false),
        filterFn: (row, columnId, filterValue) => compareDates(new Date(row.getValue(columnId) * 1000), filterValue.$d),
        minSize: 150,
        grow: 1,
      },
    ];

    if (isMpp || isCouncillor) {
      return [
        {
          accessorKey: 'status',
          header: 'Status',
          minSize: 70,
          grow: 1,
          filterVariant: 'select',
        },
        ...baseColumns,
      ];
    }

    if (userInfo && (userInfo.role === stafferRoleValue || userInfo.role === managerRoleValue)) {
      return [
        {
          accessorFn: (row) => {
            const { status } = row;
            return status === 'Unread' || status === 'Read' ? 'Unhandled' : status;
          },
          id: 'status',
          header: 'Status',
          minSize: 70,
          grow: 1,
          filterVariant: 'select',
        },
        ...baseColumns,
      ];
    }

    return baseColumns;
  }, [userInfo]);

  return (
    <div className="dashboard">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MessagesTable data={sortedFeedback} columns={columns} loading={loading} />
      </LocalizationProvider>
    </div>
  );
};

export default Dashboard;
